import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PersonIcon from '@mui/icons-material/Person';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import { baseUrl } from 'Api';
import styles from './AffiliateMemberDropdown.module.css';

export const AffiliateMemberDropdown = ({ selectedMember, onSelectMember, getToken }) => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchMembers = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const apiUrl = `${baseUrl}/managerUsersList`;
      const requestBody = { token };

      const response = await axios.post(apiUrl, requestBody);

      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        // Filter only members with completed profiles
        const completedMembers = response.data.data.filter(
          (member) => member.profile_status === 'Completed',
        );
        setMembers(completedMembers);
      } else {
        setError('No members found');
        setMembers([]);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching members:', err);
      setError('Failed to load members');
      setMembers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line
  }, []);

  const handleSelect = (member) => {
    onSelectMember(member);
    setIsOpen(false);
    setSearchQuery('');
  };

  const toggleDropdown = () => {
    if (!isLoading && members.length > 0) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchQuery('');
      }
    }
  };

  const filteredMembers = members.filter((member) => {
    const searchLower = searchQuery.toLowerCase();
    const displayName = `${member.firstname || ''} ${member.lastname || ''}`.trim().toLowerCase();
    const email = member.email.toLowerCase();
    return displayName.includes(searchLower) || email.includes(searchLower);
  });

  const handleClickOutside = (e) => {
    if (!e.target.closest(`.${styles.dropdown}`)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    return undefined;
    // eslint-disable-next-line
  }, [isOpen]);

  if (isLoading) {
    return (
      <div className={styles.dropdown}>
        <div className={`${styles.dropdownButton} ${styles.dropdownButtonDisabled}`}>
          <PersonIcon style={{ fontSize: 20 }} />
          <span>Loading members...</span>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  if (error || members.length === 0) {
    return (
      <div className={styles.dropdown}>
        <div className={`${styles.dropdownButton} ${styles.dropdownButtonDisabled}`}>
          <PersonIcon style={{ fontSize: 20 }} />
          <span>{error || 'No members available'}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dropdown}>
      <button
        className={`${styles.dropdownButton} ${isOpen ? styles.dropdownButtonOpen : ''} ${selectedMember ? styles.dropdownButtonSelected : ''}`}
        onClick={toggleDropdown}
      >
        <PersonIcon style={{ fontSize: 20 }} />
        <span className={styles.dropdownButtonText}>
          {selectedMember ? (
            `${selectedMember.firstname || ''} ${selectedMember.lastname || ''}`.trim() || selectedMember.email
          ) : (
            'Select a member'
          )}
        </span>
        <ArrowDropDownIcon
          className={`${styles.dropdownArrow} ${isOpen ? styles.dropdownArrowOpen : ''}`}
        />
      </button>

      {isOpen && (
        <div className={styles.dropdownMenu}>
          <div className={styles.dropdownHeader}>
            <span className={styles.dropdownHeaderText}>Select Member</span>
            <span className={styles.dropdownHeaderCount}>
              {filteredMembers.length}
              {' '}
              member
              {filteredMembers.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className={styles.searchContainer}>
            <SearchIcon className={styles.searchIcon} style={{ fontSize: 18 }} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.dropdownList}>
            {filteredMembers.length === 0 ? (
              <div className={styles.emptyState}>
                <PersonIcon style={{ fontSize: 48, color: '#ccc' }} />
                <p>No members found</p>
              </div>
            ) : (
              filteredMembers.map((member) => {
                const isSelected = selectedMember?.userId === member.userId;
                const displayName = `${member.firstname || ''} ${member.lastname || ''}`.trim();

                return (
                  <button
                    key={member.userId}
                    className={`${styles.dropdownItem} ${isSelected ? styles.dropdownItemSelected : ''}`}
                    onClick={() => handleSelect(member)}
                  >
                    <div className={styles.memberAvatar}>
                      <PersonIcon style={{ fontSize: 18 }} />
                    </div>
                    <div className={styles.memberInfo}>
                      <div className={styles.memberName}>
                        {displayName || 'No Name'}
                      </div>
                      <div className={styles.memberEmail}>{member.email}</div>
                    </div>
                    {isSelected && (
                      <div className={styles.checkIcon}>
                        <CheckIcon style={{ fontSize: 18 }} />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
